import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
	TextField,
	InputAdornment,
	Button,
	Select,
	MenuItem,
	Autocomplete,
	FormControl,
	InputLabel,
	IconButton,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions
} from "@mui/material";
import { Search, Close, FilterList } from "@mui/icons-material";
import { THEME_MAP } from "../theme";
import { useIsMobile } from "../hooks/useIsMobile";

export function BarsFilters({
	searchTerm,
	appliedSearchTerm,
	onSearchChange,
	cityOptions,
	selectedCity,
	appliedCity,
	onCityChange,
	stateRegions,
	selectedStateId,
	appliedStateId,
	selectedRegionId,
	appliedRegionId,
	selectedSubregionId,
	appliedSubregionId,
	onStateChange,
	onRegionChange,
	onSubregionChange,
	radiusMiles,
	appliedRadiusMiles,
	onRadiusChange,
	onClearFilters,
	onApplyFilters,
	applyDisabled,
	onRemoveFilter
}) {
	const isMobile = useIsMobile();
	const [radiusInput, setRadiusInput] = useState(radiusMiles || "");
	const [radiusApplied, setRadiusApplied] = useState(Boolean(radiusMiles));
	const [filterModalOpen, setFilterModalOpen] = useState(false);

	// keep radius input in sync with prop
	useEffect(() => {
		setRadiusInput(radiusMiles || "");
		setRadiusApplied(Boolean(radiusMiles));
	}, [radiusMiles]);

	const radiusInputNumber = Number.parseFloat(radiusInput);
	const radiusInputValid = radiusInput === "" || (Number.isFinite(radiusInputNumber) && radiusInputNumber >= 0);
	const hasCitySelection = Boolean(selectedCity);
	const canApplyRadius = hasCitySelection && radiusInputValid;

	const handleCityChangeInternal = useCallback((event, option) => {
		onCityChange(event, option);
		if (!option) {
			setRadiusInput("");
			onRadiusChange("");
			setRadiusApplied(false);
		}
	}, [onCityChange, onRadiusChange]);

	const handleRadiusApply = useCallback(() => {
		if (!radiusInputValid) {
			setRadiusApplied(false);
			return;
		}
		if (radiusInput === "") {
			onRadiusChange("");
			setRadiusApplied(false);
			return;
		}
		const nextValue = Number.parseFloat(radiusInput);
		onRadiusChange(Number.isFinite(nextValue) ? String(nextValue) : "");
		setRadiusApplied(true);
	}, [onRadiusChange, radiusInput, radiusInputValid]);

	const handleRadiusClear = useCallback(() => {
		setRadiusInput("");
		onRadiusChange("");
		setRadiusApplied(false);
	}, [onRadiusChange]);

	const handleRadiusKeyPress = useCallback((event) => {
		if (event.key === "Enter" && canApplyRadius) {
			handleRadiusApply();
		}
	}, [canApplyRadius, handleRadiusApply]);

	const activeFilters = useMemo(() => {
		const filters = [];
		if (appliedSearchTerm) {
			filters.push({ type: "search", label: `Search: "${appliedSearchTerm}"` });
		}
		if (appliedStateId) {
			const state = stateRegions.find((item) => item.id === appliedStateId);
			filters.push({ type: "state", label: `State: ${state?.name || appliedStateId}` });
		}
		if (appliedRegionId) {
			const region = stateRegions
				.find((state) => state.id === appliedStateId)
				?.regions?.find((item) => item.id === appliedRegionId);
			filters.push({ type: "region", label: `Region: ${region?.name || appliedRegionId}` });
		}
		if (appliedSubregionId) {
			const region = stateRegions
				.find((state) => state.id === appliedStateId)
				?.regions?.find((item) => item.id === appliedRegionId);
			const subregion = region?.subregions?.find((item) => item.id === appliedSubregionId);
			filters.push({ type: "subregion", label: `Subregion: ${subregion?.name || appliedSubregionId}` });
		}
		if (appliedCity) {
			filters.push({ type: "city", label: `City: ${appliedCity.label}` });
		}
		if (radiusApplied && appliedRadiusMiles) {
			filters.push({ type: "radius", label: `Radius: ${appliedRadiusMiles} mi` });
		}
		return filters;
	}, [appliedCity, appliedRadiusMiles, appliedRegionId, appliedSearchTerm, appliedStateId, appliedSubregionId, radiusApplied, stateRegions]);

	// Count non-search active filters for the badge
	const nonSearchFilterCount = useMemo(
		() => activeFilters.filter((f) => f.type !== "search").length,
		[activeFilters]
	);

	const handleRemoveFilter = useCallback((filterType) => {
		if (onRemoveFilter) {
			onRemoveFilter(filterType);
			return;
		}
		switch (filterType) {
			case "search":
				onSearchChange({ target: { value: "" } });
				break;
			case "state":
				onStateChange({ target: { value: "" } });
				break;
			case "region":
				onRegionChange({ target: { value: "" } });
				break;
			case "subregion":
				onSubregionChange({ target: { value: "" } });
				break;
			case "city":
				handleCityChangeInternal(null, null);
				break;
			case "radius":
				handleRadiusClear();
				break;
			default:
				break;
		}
	}, [handleCityChangeInternal, handleRadiusClear, onRegionChange, onRemoveFilter, onSearchChange, onStateChange, onSubregionChange]);

	const handleModalApply = useCallback(() => {
		onApplyFilters();
		setFilterModalOpen(false);
	}, [onApplyFilters]);

	const handleModalClear = useCallback(() => {
		onClearFilters();
		setFilterModalOpen(false);
	}, [onClearFilters]);

	const filterControls = (
		<>
			<FormControl className="region-select" variant="outlined" size="small" fullWidth={isMobile}>
				<InputLabel id="state-filter-label">State</InputLabel>
				<Select
					labelId="state-filter-label"
					label="State"
					value={selectedStateId || ""}
					onChange={onStateChange}
				>
					<MenuItem value="">All States</MenuItem>
					{stateRegions
						.filter((state) => state.name && typeof state.name === "string")
						.map((state) => (
							<MenuItem key={state.id} value={state.id}>
								{state.name}{state.abbr ? ` (${state.abbr})` : ""}
							</MenuItem>
						))}
				</Select>
			</FormControl>

			<FormControl className="region-select" variant="outlined" size="small" fullWidth={isMobile}>
				<InputLabel id="region-filter-label">Region</InputLabel>
				<Select
					labelId="region-filter-label"
					label="Region"
					value={selectedRegionId || ""}
					onChange={onRegionChange}
					disabled={!selectedStateId}
				>
					<MenuItem value="">All Regions</MenuItem>
					{(stateRegions.find((state) => state.id === selectedStateId)?.regions || [])
						.filter((region) => region.name && typeof region.name === "string")
						.map((region) => (
							<MenuItem key={region.id} value={region.id}>
								{region.name}
							</MenuItem>
						))}
				</Select>
			</FormControl>

			<FormControl className="region-select" variant="outlined" size="small" fullWidth={isMobile}>
				<InputLabel id="subregion-filter-label">Subregion</InputLabel>
				<Select
					labelId="subregion-filter-label"
					label="Subregion"
					value={selectedSubregionId || ""}
					onChange={onSubregionChange}
					disabled={!selectedStateId || !selectedRegionId}
				>
					<MenuItem value="">All Subregions</MenuItem>
					{(stateRegions
						.find((state) => state.id === selectedStateId)
						?.regions?.find((region) => region.id === selectedRegionId)
						?.subregions || [])
						.filter((subregion) => subregion.name && typeof subregion.name === "string")
						.map((subregion) => (
							<MenuItem key={subregion.id} value={subregion.id}>
								{subregion.name}
							</MenuItem>
						))}
				</Select>
			</FormControl>

			<Autocomplete
				className="city-autocomplete"
				options={cityOptions}
				getOptionLabel={(option) => option?.label || ""}
				isOptionEqualToValue={(option, value) => option?.key === value?.key}
				value={selectedCity}
				onChange={handleCityChangeInternal}
				clearOnEscape
				size="small"
				fullWidth={isMobile}
				renderInput={(params) => (
					<TextField
						{...params}
						label="City"
						placeholder="Filter by city"
						variant="outlined"
					/>
				)}
			/>

			{selectedCity && (
				<>
					<TextField
						className="radius-field"
						label="Radius (optional)"
						type="number"
						variant="outlined"
						size="small"
						fullWidth={isMobile}
						value={radiusInput}
						onChange={(event) => setRadiusInput(event.target.value)}
						onKeyPress={handleRadiusKeyPress}
						placeholder="Miles"
						inputProps={{ min: 0, step: 1, inputMode: "decimal" }}
						autoComplete="off"
						error={!radiusInputValid}
					/>

					{!radiusApplied ? (
						<Button
							variant="outlined"
							size="small"
							onClick={handleRadiusApply}
							disabled={!canApplyRadius}
							className="radius-action-btn"
							fullWidth={isMobile}
						>
							Apply Radius
						</Button>
					) : (
						<Button
							variant="outlined"
							size="small"
							onClick={handleRadiusClear}
							className="radius-action-btn"
							fullWidth={isMobile}
						>
							Clear Radius
						</Button>
					)}
				</>
			)}
		</>
	);

	return (
		<Container>
			{isMobile ? (
				<>
					<MobileSearchRow>
						<TextField
							className="search-field"
							label="Search Bars"
							placeholder="Search by name"
							variant="outlined"
							size="small"
							value={searchTerm}
							onChange={onSearchChange}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search fontSize="small" />
									</InputAdornment>
								)
							}}
						/>

						<Button
							variant="contained"
							size="small"
							onClick={onApplyFilters}
							disabled={applyDisabled}
							className="apply-btn"
						>
							Apply
						</Button>

						<IconButton
							onClick={() => setFilterModalOpen(true)}
							className="filter-icon-btn"
							size="small"
						>
							<FilterList />
							{nonSearchFilterCount > 0 && (
								<span className="filter-badge">{nonSearchFilterCount}</span>
							)}
						</IconButton>
					</MobileSearchRow>

					<Dialog
						open={filterModalOpen}
						onClose={() => setFilterModalOpen(false)}
						fullWidth
						maxWidth="sm"
						PaperProps={{ sx: { borderRadius: "12px" } }}
					>
						<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
							Filters
							<IconButton size="small" onClick={() => setFilterModalOpen(false)}>
								<Close />
							</IconButton>
						</DialogTitle>
						<DialogContent>
							<ModalFilterStack>
								{filterControls}
							</ModalFilterStack>
						</DialogContent>
						<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
							<Button variant="outlined" size="small" onClick={handleModalClear}>
								Clear All
							</Button>
							<Button variant="contained" size="small" onClick={handleModalApply} disabled={applyDisabled}>
								Apply Filters
							</Button>
						</DialogActions>
					</Dialog>
				</>
			) : (
				<FilterRow>
					<TextField
						className="search-field"
						label="Search Bars"
						placeholder="Search by name"
						variant="outlined"
						size="small"
						value={searchTerm}
						onChange={onSearchChange}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search fontSize="small" />
								</InputAdornment>
							)
						}}
					/>

					{filterControls}

					<Button
						variant="contained"
						size="small"
						onClick={onApplyFilters}
						disabled={applyDisabled}
						className="apply-btn"
					>
						Apply
					</Button>

					<Button
						variant="outlined"
						size="small"
						onClick={onClearFilters}
						className="clear-btn"
					>
						Clear
					</Button>
				</FilterRow>
			)}

			{activeFilters.length > 0 && (
				<ChipsRow>
					{activeFilters.map((filter) => (
						<Chip
							key={filter.type}
							label={filter.label}
							onDelete={() => handleRemoveFilter(filter.type)}
							deleteIcon={<Close />}
							size="small"
							className="filter-chip"
						/>
					))}
				</ChipsRow>
			)}
		</Container>
	);
}

const Container = styled.div`
	margin-bottom: 16px;
`;

const MobileSearchRow = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;

	.search-field {
		flex: 1;
		min-width: 0;
	}

	.apply-btn {
		white-space: nowrap;
		flex-shrink: 0;
	}

	.filter-icon-btn {
		position: relative;
		color: ${THEME_MAP["color-basic-700"]};
		flex-shrink: 0;
	}

	.filter-badge {
		position: absolute;
		top: -2px;
		right: -2px;
		background: #1976d2;
		color: white;
		font-size: 10px;
		font-weight: 700;
		width: 16px;
		height: 16px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		line-height: 1;
	}
`;

const ModalFilterStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding-top: 8px;
`;

const FilterRow = styled.div`
	display: flex;
	gap: 10px;
	align-items: center;
	flex-wrap: nowrap;
	background: ${THEME_MAP["color-basic-100"]};
	border-radius: 8px;
	padding: 12px 8px 12px 0;
	overflow-x: auto;

	.search-field {
		min-width: 200px;
		max-width: 250px;
	}

	.region-select {
		min-width: 160px;
		max-width: 180px;
	}

	.city-autocomplete {
		min-width: 150px;
		max-width: 170px;
	}

	.radius-field {
		min-width: 120px;
		max-width: 140px;
	}

	.radius-action-btn {
		white-space: nowrap;
	}

	.sort-select {
		min-width: 140px;
		max-width: 160px;
	}

	.clear-btn {
		white-space: nowrap;
	}

	.apply-btn {
		white-space: nowrap;
	}
`;

const ChipsRow = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
	flex-wrap: wrap;
	margin-top: 12px;

	.filter-chip {
		background: ${THEME_MAP["color-primary-100"]};
		color: ${THEME_MAP["color-primary-700"]};
		font-weight: 500;
	}
`;
